import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { AppRouter } from '@/routing/AppRouter';
import { QueryProvider } from '@/providers/QueryProvider';
import { NotificationProvider } from '@/components/shared/NotificationProvider';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { createAppTheme } from '@/theme';
import { EntityActionDialogProvider } from '@/components/entities/EntityActionDialogProvider';
import { AccessibilityStyles } from '@/components/accessibility';
import { useAuthStore } from '@/stores/useAuthStore';
import { useNotifications } from '@/stores/useNotificationStore';
import { useThemePrefsStore } from '@/stores/useThemePrefsStore';
import { logger } from '@equip/utils/logger';
import '@/actions/registerDefaultEntityActions';
import '@/actions/registerLocationEntityActions';
import '@/actions/registerPersonEntityActions';
import '@/components/entities'; // Register entity detail renderers
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import { setReportFooterText } from '@/reports/reportFooter';

/** Syncs the report footer text from module config into the shared report constant. */
const ReportConfigSync: React.FC = () => {
  const { config } = useModuleConfig('reports');
  const footerText = (config.features?.reports as Record<string, string> | undefined)
    ?.report_footer_text;

  useEffect(() => {
    if (footerText) setReportFooterText(footerText);
  }, [footerText]);

  return null;
};

export const App: React.FC = () => {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const notifications = useNotifications();
  const resolvedMode = useThemePrefsStore((state) => state.resolvedMode);
  const appTheme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  // Initialize auth on app mount - attempts silent SSO if not authenticated
  useEffect(() => {
    initializeAuth().catch((error) => {
      logger.error('Failed to initialize authentication', {
        component: 'App',
        error: String(error),
      });
      // Don't block the app, just log the error
      // User can still manually navigate to /login
    });
  }, [initializeAuth]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sessionKey = 'rate-limit-banner-last-shown-at';
    const COOLDOWN_MS = 60_000;

    const handleRateLimit = (event: Event) => {
      const rateLimitEvent = event as CustomEvent<{ retryAfter?: string | null }>;
      const retryAfterSeconds = Number(rateLimitEvent.detail?.retryAfter ?? '');
      const cooldownMs =
        Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
          ? retryAfterSeconds * 1000
          : COOLDOWN_MS;

      const now = Date.now();
      const lastShownAt = Number(sessionStorage.getItem(sessionKey) ?? '0');
      if (Number.isFinite(lastShownAt) && now - lastShownAt < cooldownMs) {
        return;
      }

      sessionStorage.setItem(sessionKey, String(now));
      notifications.warning(
        'Rate limit reached',
        'Please wait one minute, then try again.',
        { persistent: true },
      );
    };

    window.addEventListener('api:rate-limited', handleRateLimit);

    return () => {
      window.removeEventListener('api:rate-limited', handleRateLimit);
    };
  }, [notifications]);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <AccessibilityStyles />
        <QueryProvider>
          <ReportConfigSync />
          <EntityActionDialogProvider>
            <AppRouter />
            <NotificationProvider />
          </EntityActionDialogProvider>
        </QueryProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};
